import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Save, Undo2, Check, AlertCircle, Search, Plus, Minus, Replace, X, RotateCcw } from "lucide-react";

// Sort helper for stable, alphabetical-by-title
const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "");

// Normalise a tags array to a comparable string (order-independent)
const tagsKey = (arr) => (Array.isArray(arr) ? [...arr].sort().join("|") : "");

export default function SuperAdminTags() {
  const { isSuperAdmin, loading } = useAuth();

  const [allowed, setAllowed] = useState(null);         // string[] of the 7 canonical tags
  const [courses, setCourses] = useState(null);         // list from /api/courses
  const [original, setOriginal] = useState({});         // slug -> string[] as returned by server
  const [draft, setDraft] = useState({});               // slug -> string[] as edited locally
  const [err, setErr] = useState("");
  const [savingSlugs, setSavingSlugs] = useState(new Set());
  const [savedFlash, setSavedFlash] = useState(new Set());
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());   // slugs currently multi-selected for bulk ops
  const [bulkMode, setBulkMode] = useState(null);        // null | "add" | "remove" | "set"
  const [bulkTags, setBulkTags] = useState([]);          // tag(s) chosen for the active bulk op

  const loadAll = async () => {
    setErr("");
    try {
      const [t, c] = await Promise.all([
        api.get("/courses/tags/allowed"),
        api.get("/courses"),
      ]);
      const allowedTags = t.data?.tags || [];
      const list = Array.isArray(c.data) ? c.data.slice().sort(byTitle) : [];
      const orig = {};
      const drf = {};
      for (const row of list) {
        const tags = Array.isArray(row.tags) ? row.tags.slice() : [];
        orig[row.slug] = tags;
        drf[row.slug] = tags.slice();
      }
      setAllowed(allowedTags);
      setCourses(list);
      setOriginal(orig);
      setDraft(drf);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load courses");
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadAll();
  }, [isSuperAdmin]);

  const dirtySlugs = useMemo(() => {
    const s = new Set();
    for (const slug of Object.keys(draft)) {
      if (tagsKey(draft[slug]) !== tagsKey(original[slug] || [])) s.add(slug);
    }
    return s;
  }, [draft, original]);

  const filtered = useMemo(() => {
    if (!courses) return [];
    const term = q.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter((c) =>
      (c.title || "").toLowerCase().includes(term) ||
      (c.slug || "").toLowerCase().includes(term)
    );
  }, [courses, q]);

  const toggleTag = (slug, tag) => {
    setDraft((prev) => {
      const cur = new Set(prev[slug] || []);
      if (cur.has(tag)) cur.delete(tag);
      else cur.add(tag);
      // Preserve allowed-order for a predictable payload
      const next = (allowed || []).filter((t) => cur.has(t));
      return { ...prev, [slug]: next };
    });
  };

  const resetRow = (slug) => {
    setDraft((prev) => ({ ...prev, [slug]: (original[slug] || []).slice() }));
  };

  const saveRow = async (slug) => {
    if (savingSlugs.has(slug)) return;
    const payload = draft[slug] || [];
    setSavingSlugs((prev) => new Set(prev).add(slug));
    setErr("");
    try {
      const res = await api.patch(`/courses/${slug}/tags`, { tags: payload });
      const serverTags = res.data?.tags || payload;
      setOriginal((prev) => ({ ...prev, [slug]: serverTags.slice() }));
      setDraft((prev) => ({ ...prev, [slug]: serverTags.slice() }));
      setSavedFlash((prev) => new Set(prev).add(slug));
      // Clear the flash after a moment
      setTimeout(() => {
        setSavedFlash((prev) => {
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
      }, 2000);
    } catch (e) {
      setErr(`${slug}: ${e.response?.data?.detail || "Could not save"}`);
    } finally {
      setSavingSlugs((prev) => {
        const next = new Set(prev);
        next.delete(slug);
        return next;
      });
    }
  };

  const saveAll = async () => {
    const targets = Array.from(dirtySlugs);
    if (targets.length === 0) return;
    if (!window.confirm(`Save tag changes for ${targets.length} course${targets.length === 1 ? "" : "s"}?`)) return;
    for (const slug of targets) {
      await saveRow(slug);
    }
  };

  const resetAll = () => {
    if (dirtySlugs.size === 0) return;
    if (!window.confirm(`Discard unsaved tag changes on ${dirtySlugs.size} course${dirtySlugs.size === 1 ? "" : "s"}?`)) return;
    const rolled = {};
    for (const slug of Object.keys(draft)) rolled[slug] = (original[slug] || []).slice();
    setDraft(rolled);
  };

  // Set every course's DRAFT to empty tags. On Save, the DB gets `[]`, which
  // trips the frontend fallback in tagsFor() → each course renders its
  // hardcoded default set (or heuristic for unmapped ones). "Default" here
  // means whatever tagsFor() would show if the DB had no custom tags at all.
  const resetAllToDefaults = async () => {
    if (!courses) return;
    const total = courses.length;
    if (!window.confirm(
      `Reset EVERY one of the ${total} courses back to Calside's default tags?\n\n` +
      `This clears all your customisations and lets the built-in per-course fallback take over. ` +
      `Nothing else on the courses is touched.`
    )) return;
    // Stage the drafts for all rows to empty
    const rolled = {};
    for (const slug of Object.keys(draft)) rolled[slug] = [];
    setDraft(rolled);
    // Commit anything actually different from server-original
    const toSave = Object.keys(rolled).filter((slug) => tagsKey(rolled[slug]) !== tagsKey(original[slug] || []));
    if (toSave.length === 0) return;
    // Fire PATCHes one at a time (idempotent, cache-friendly)
    for (const slug of toSave) {
      await saveRow(slug);
    }
  };

  // ---- Multi-select + bulk operations -------------------------------------
  const toggleSelected = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const selectAllVisible = () => {
    // We compute `filtered` below in JSX; recompute here for the same set.
    const term = q.trim().toLowerCase();
    const rows = (courses || []).filter((c) =>
      !term ||
      (c.title || "").toLowerCase().includes(term) ||
      (c.slug || "").toLowerCase().includes(term)
    );
    setSelected(new Set(rows.map((r) => r.slug)));
  };
  const clearSelection = () => setSelected(new Set());

  const openBulk = (mode) => {
    setBulkMode(mode);
    setBulkTags([]);
  };
  const closeBulk = () => {
    setBulkMode(null);
    setBulkTags([]);
  };
  const toggleBulkTag = (tag) => {
    setBulkTags((prev) => {
      // For "add" and "remove" modes: single-select. For "set": multi-select.
      if (bulkMode === "set") {
        return prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag];
      }
      return prev.includes(tag) ? [] : [tag];
    });
  };

  // Apply the pending bulk op to the DRAFT of every selected slug.
  // Does NOT write to the server. User still clicks Save-all to commit.
  const applyBulk = () => {
    if (!bulkMode || selected.size === 0) return;
    if (bulkMode !== "set" && bulkTags.length === 0) return;

    setDraft((prev) => {
      const next = { ...prev };
      for (const slug of selected) {
        const current = new Set(next[slug] || []);
        if (bulkMode === "add") {
          for (const t of bulkTags) current.add(t);
        } else if (bulkMode === "remove") {
          for (const t of bulkTags) current.delete(t);
        } else if (bulkMode === "set") {
          current.clear();
          for (const t of bulkTags) current.add(t);
        }
        // Preserve allowed-order
        next[slug] = (allowed || []).filter((t) => current.has(t));
      }
      return next;
    });
    closeBulk();
  };

  if (loading) return <Layout><div className="min-h-[60vh]" /></Layout>;
  if (!isSuperAdmin) return <Navigate to="/admin" replace />;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-2">Private · super admin</div>
            <h1 className="font-serif text-4xl text-brand-ink">Bulk tag editor</h1>
            <p className="text-brand-body mt-2 max-w-2xl">
              Toggle the {allowed?.length || 7} canonical tags on any course. Changes stay local until you click Save on a row or Save all. Nothing else on the course is touched.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-sm text-brand-muted hover:text-brand-ink underline underline-offset-2"
              data-testid="tags-back-to-admin"
            >
              ← Back to admin
            </Link>
          </div>
        </div>

        {err && (
          <div className="mb-4 flex items-start gap-2 text-sm bg-brand-accent/10 border border-brand-accent/30 text-brand-ink rounded-2xl px-4 py-3" data-testid="tags-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.7} />
            <span>{err}</span>
          </div>
        )}

        <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-brand-bone/95 backdrop-blur border-b border-brand-linen flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.7} />
            <input
              type="text"
              placeholder="Filter by course title or slug…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-white border border-brand-linen rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-brand-ink"
              data-testid="tags-filter-input"
            />
          </div>
          <div className="text-xs text-brand-muted whitespace-nowrap">
            {dirtySlugs.size > 0 ? (
              <span data-testid="tags-dirty-count"><strong>{dirtySlugs.size}</strong> unsaved</span>
            ) : (
              <span>All changes saved</span>
            )}
          </div>
          <button
            type="button"
            onClick={resetAllToDefaults}
            disabled={!courses || savingSlugs.size > 0}
            data-testid="tags-reset-all-to-defaults"
            className="inline-flex items-center gap-1.5 border border-brand-accent/40 text-brand-accent rounded-full px-4 py-2 text-sm hover:bg-brand-accent/10 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Clear tags on every course so the built-in defaults show through"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.7} /> Reset all to defaults
          </button>
          <button
            type="button"
            onClick={resetAll}
            disabled={dirtySlugs.size === 0}
            data-testid="tags-reset-all"
            className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Undo2 className="w-3.5 h-3.5" strokeWidth={1.7} /> Discard unsaved
          </button>
          <button
            type="button"
            onClick={saveAll}
            disabled={dirtySlugs.size === 0}
            data-testid="tags-save-all"
            className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save className="w-3.5 h-3.5" strokeWidth={1.7} /> Save all
          </button>
        </div>

        {/* Bulk-action bar — appears when any rows are selected */}
        <div className="mt-3 flex flex-wrap gap-2 items-center text-sm">
          <button
            type="button"
            onClick={selectAllVisible}
            data-testid="tags-select-all-visible"
            className="text-brand-muted hover:text-brand-ink underline underline-offset-2"
          >
            Select all visible ({filtered.length})
          </button>
          {selected.size > 0 && (
            <>
              <span className="text-brand-muted">·</span>
              <span data-testid="tags-selected-count">
                <strong>{selected.size}</strong> selected
              </span>
              <button
                type="button"
                onClick={clearSelection}
                data-testid="tags-clear-selection"
                className="text-brand-muted hover:text-brand-ink underline underline-offset-2 ml-1"
              >
                clear
              </button>
              <span className="text-brand-muted mx-2">·</span>
              <button
                type="button"
                onClick={() => openBulk("add")}
                data-testid="tags-bulk-add"
                className="inline-flex items-center gap-1 bg-brand-ink text-brand-bone rounded-full px-3 py-1 hover:bg-brand-inkHover text-xs"
              >
                <Plus className="w-3 h-3" strokeWidth={2} /> Add tag to selected
              </button>
              <button
                type="button"
                onClick={() => openBulk("remove")}
                data-testid="tags-bulk-remove"
                className="inline-flex items-center gap-1 border border-brand-linen text-brand-ink rounded-full px-3 py-1 hover:bg-brand-linen text-xs"
              >
                <Minus className="w-3 h-3" strokeWidth={2} /> Remove tag from selected
              </button>
              <button
                type="button"
                onClick={() => openBulk("set")}
                data-testid="tags-bulk-set"
                className="inline-flex items-center gap-1 border border-brand-linen text-brand-ink rounded-full px-3 py-1 hover:bg-brand-linen text-xs"
              >
                <Replace className="w-3 h-3" strokeWidth={2} /> Replace tags on selected
              </button>
            </>
          )}
        </div>

        {!courses ? (
          <div className="mt-8 text-brand-muted">Loading courses…</div>
        ) : (
          <div className="mt-6 space-y-2" data-testid="tags-course-list">
            {filtered.map((c) => {
              const dirty = dirtySlugs.has(c.slug);
              const saving = savingSlugs.has(c.slug);
              const flashed = savedFlash.has(c.slug);
              const current = new Set(draft[c.slug] || []);
              return (
                <div
                  key={c.slug}
                  data-testid={`tag-row-${c.slug}`}
                  className={`bg-white border rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 transition-colors ${
                    dirty ? "border-brand-secondary/60" : selected.has(c.slug) ? "border-brand-ink/40 bg-brand-linen/30" : "border-brand-linen"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.slug)}
                    onChange={() => toggleSelected(c.slug)}
                    data-testid={`tag-select-${c.slug}`}
                    aria-label={`Select ${c.title}`}
                    className="w-4 h-4 rounded border-brand-linen accent-brand-ink shrink-0"
                  />
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-medium text-brand-ink leading-tight">
                      {c.title}
                      {c.community === false && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-brand-muted bg-brand-bone border border-brand-linen rounded-full px-2 py-0.5 align-middle">
                          practice
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-brand-muted font-mono mt-0.5">{c.slug}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(allowed || []).map((tag) => {
                      const on = current.has(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(c.slug, tag)}
                          disabled={saving}
                          data-testid={`tag-toggle-${c.slug}-${tag.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                            on
                              ? "bg-brand-ink text-brand-bone border-brand-ink"
                              : "bg-white text-brand-body border-brand-linen hover:border-brand-ink/40"
                          } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {flashed && (
                      <span className="inline-flex items-center gap-1 text-xs text-brand-secondary" data-testid={`tag-saved-${c.slug}`}>
                        <Check className="w-3.5 h-3.5" strokeWidth={2} /> Saved
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => resetRow(c.slug)}
                      disabled={!dirty || saving}
                      data-testid={`tag-reset-${c.slug}`}
                      className="text-xs text-brand-muted hover:text-brand-ink disabled:opacity-30 disabled:cursor-not-allowed underline underline-offset-2"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => saveRow(c.slug)}
                      disabled={!dirty || saving}
                      data-testid={`tag-save-${c.slug}`}
                      className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-4 py-1.5 text-xs hover:bg-brand-inkHover disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {saving ? "Saving…" : (<><Save className="w-3 h-3" strokeWidth={1.7} /> Save</>)}
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-brand-muted text-sm italic px-4 py-6" data-testid="tags-empty-state">
                No courses match “{q}”.
              </div>
            )}
          </div>
        )}

        <div className="mt-8 text-xs text-brand-muted italic">
          Editing tags only. Course content, images, quizzes, flashcards and progress are untouched. Nothing is saved until you click Save.
        </div>
      </div>

      {/* Bulk-op modal — pick which tag(s) to add / remove / replace across selected courses */}
      {bulkMode && (
        <div
          className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeBulk}
          data-testid="tags-bulk-modal-backdrop"
        >
          <div
            className="bg-white rounded-3xl shadow-cardHover max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeBulk}
              className="absolute top-4 right-4 text-brand-muted hover:text-brand-ink"
              aria-label="Close"
            >
              <X className="w-5 h-5" strokeWidth={1.7} />
            </button>
            <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-2">
              Bulk · {selected.size} course{selected.size === 1 ? "" : "s"} selected
            </div>
            <h3 className="font-serif text-2xl text-brand-ink">
              {bulkMode === "add" && "Add tag to selected"}
              {bulkMode === "remove" && "Remove tag from selected"}
              {bulkMode === "set" && "Replace tags on selected"}
            </h3>
            <p className="text-sm text-brand-body mt-2 leading-relaxed">
              {bulkMode === "add" && "Pick the tag to add. Courses that already have it are unchanged."}
              {bulkMode === "remove" && "Pick the tag to strip. Courses that don't have it are unchanged."}
              {bulkMode === "set" && "Pick the exact set of tags — every selected course will end up with EXACTLY these tags. Existing tags will be replaced."}
              {" Nothing is saved until you click Save all afterwards."}
            </p>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="tags-bulk-picker">
              {(allowed || []).map((tag) => {
                const on = bulkTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleBulkTag(tag)}
                    data-testid={`tags-bulk-pick-${tag.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      on
                        ? "bg-brand-ink text-brand-bone border-brand-ink"
                        : "bg-white text-brand-body border-brand-linen hover:border-brand-ink/40"
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button
                type="button"
                onClick={closeBulk}
                data-testid="tags-bulk-cancel"
                className="border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyBulk}
                disabled={bulkMode !== "set" && bulkTags.length === 0}
                data-testid="tags-bulk-apply"
                className="bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply to {selected.size}
              </button>
            </div>

            <div className="mt-4 text-[11px] text-brand-muted italic">
              Bulk apply updates each selected course&apos;s <em>draft</em> tags. Review the highlighted rows, then click <strong>Save all</strong> at the top to commit.
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
